import { IconButton, Stack, Tooltip, Typography } from '@mui/material'
import {
  Close as CloseIcon,
  Link as LinkIcon,
  LinkOff as LinkOffIcon,
  PeopleOutline as PeopleOutlineIcon,
} from '@mui/icons-material'
import React from 'react'
import { Appointment } from '../../types/treatments.types'
import { useTranslation } from '../../../../../common/hooks'
import { useStoreActions } from '../../../../../store/store.hooks'
import { useSelectedLanguage } from '../../../../../common/hooks/useSelectedLanguage'
import { AppointmentActions } from './AppointmentActions'
import { BetmenListItemCardCell } from '../../../../../common/components'
import { AppointmentStatusComponent } from '../../../../../common/components/Dashboard/AppointmentStatus'
import { useTreatmentPanelData } from '../TreatmentPanel/TreatmentPanelProvider'

type AppointmentCardProps = {
  appointment: Appointment
  isPast: boolean
  editAppointment: (appointment: Appointment) => void
  cloneAppointment: (appointment: Appointment) => void
  linking?: Appointment | null
  disableLinking: boolean
  canLinkTo: boolean
  onLink?: () => void
}

export const AppointmentCard: React.FC<AppointmentCardProps> = ({
  appointment,
  isPast,
  editAppointment,
  cloneAppointment,
  linking = null,
  disableLinking,
  canLinkTo,
  onLink = () => null,
}) => {
  const { isTreatmentDraft } = useTreatmentPanelData()
  const { t } = useTranslation()
  const setToast = useStoreActions((actions) => actions.toast.setToast)

  const date = appointment?.proposedDate
  const dateString = t('common:intlDateFormatted', { date })
  const timeString = t('common:patientFormattedTime', { date })
  const shouldDisplayTime =
    appointment?.isBetmenBooked ?? !!appointment?.eesztBookingId
  const dateTimeString = `${dateString}${
    shouldDisplayTime ? `, ${timeString}` : ''
  }`
  const selectedLanguage = useSelectedLanguage()

  const linkingToPast = linking && linking.id !== appointment.id && isPast
  const canNotConnectToThis =
    disableLinking || (linkingToPast && !isTreatmentDraft)

  const handleLink: React.MouseEventHandler<HTMLButtonElement> = (ev) => {
    ev.stopPropagation()

    if (disableLinking) {
      setToast({
        text: t('protocol:dependency.error.circularDependency'),
        type: 'error',
      })
    } else if (linkingToPast && !isTreatmentDraft) {
      setToast({
        text: t('treatment:connectingToPastItems'),
        type: 'error',
      })
    } else {
      onLink()
    }
  }

  return (
    <>
      <BetmenListItemCardCell gridSize={1}>
        <PeopleOutlineIcon fontSize="large" />
      </BetmenListItemCardCell>

      <BetmenListItemCardCell gridSize={4}>
        {appointment?.info?.doctorTitle[selectedLanguage]}
      </BetmenListItemCardCell>

      <BetmenListItemCardCell
        gridSize={3}
        typographyProps={{ fontWeight: 400 }}
      >
        {t('common:formattedNameFull', appointment.doctor)}
      </BetmenListItemCardCell>

      <BetmenListItemCardCell gridSize={3}>
        <Stack direction="row" gap={1}>
          {!isPast && (
            <AppointmentStatusComponent status={appointment.status} />
          )}
          <Typography variant="body2" color="textSecondary">
            {dateTimeString}
          </Typography>
        </Stack>
      </BetmenListItemCardCell>

      <BetmenListItemCardCell
        gridSize={1}
        display="flex"
        justifyContent="flex-end"
        alignItems={'center'}
        flexDirection={'row'}
        gap={1}
      >
        {appointment && (
          <Tooltip
            title={t('doctor:tooltips.link') as string}
            enterDelay={500}
            leaveDelay={200}
          >
            <IconButton
              color={canLinkTo && !canNotConnectToThis ? 'primary' : 'default'}
              onClick={handleLink}
              size="large"
            >
              {canNotConnectToThis ? (
                <LinkOffIcon />
              ) : canLinkTo || !linking ? (
                <LinkIcon />
              ) : (
                <CloseIcon />
              )}
            </IconButton>
          </Tooltip>
        )}

        <AppointmentActions
          appointment={appointment}
          isTreatmentItemPast={isPast}
          editAppointment={editAppointment}
          cloneAppointment={cloneAppointment}
        />
      </BetmenListItemCardCell>
    </>
  )
}
